import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Typography, Slider, Row, Col, Form, message, Tooltip, Space } from 'antd';
import { useSelector, useDispatch } from "react-redux";
import { BarChartOutlined, SettingOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import practiceClient from "../../client/PracticeClient";
import { setPractice } from '../../redux/actions/practiceActions';
import { setExam } from '../../redux/actions/examActions';
import { QUIZ_PRACTICE_TYPE, QUIZ_PRACTICE_FORMAT, QUIZ_LEARNING_METHOD } from '../../constants/quizPractice';
import LearningMethodPanel from '../../components/Practice/LearningMethodPanel';
import LearningSettingsPanel from '../../components/Practice/LearningSettingsPanel';
import DeadlinePanel from '../../components/Practice/DeadlinePanel';
import useTranslation from '../../hooks/useTranslation';
import useDeckPracticeSummary from '../../hooks/useDeckPracticeSummary';

const { Title, Paragraph, Text } = Typography;
const QuizLearnPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Получаем выбранную колоду из Redux
    const selectedDeck = useSelector((state) => state.deck.selectedDeck);

    // Практика больше не открывается при показе страницы: страница выбора только
    // читает сводку, а init остаётся на кнопке. Идентификатор берётся из сводки, если
    // практику уже запускали, — он нужен только ссылке на статистику.
    const [practiceId, setPracticeId] = useState(null);

    // Состояние для карточки Practice
    const [poolDTO, setPoolDTO] = useState({
        startQuizNumber: 1,
        finishQuizNumber: 14,
        poolSize: 5,
        correctAnswerLevel: 5,
        totalQuizzes: 0,
    });

    // Состояние для карточки Real
    // Диапазон по умолчанию доводится до размера колоды, когда придёт сводка: числа
    // «65» и «290» были поставлены наугад, и на колоде из сорока вопросов ползунок
    // стоял за пределами собственной шкалы.
    const [realDTO, setRealDTO] = useState({
        examDuration: 120,
        numberOfQuestions: 20,
        questionRange: [1, 1],
    });

    const [isLoading, setIsLoading] = useState(false);
    const [isStartingPlanned, setIsStartingPlanned] = useState(false);

    // Сводка — чтение: сколько вопросов в колоде и какие параметры отбора записаны у
    // уже начатой практики. Ни одного init на показ страницы.
    const { summary, reload } = useDeckPracticeSummary(
        selectedDeck?.deckId, QUIZ_PRACTICE_TYPE, t('practice.summaryFailed'));
    // Методику колоды выбирает панель ниже. По умолчанию — та, что действовала до
    // появления выбора: вопросы шли по числу верных.
    // Изначально null: панель грузит выбор своим запросом, и до его конца открывать
    // практику нечем — иначе она создалась бы методикой по умолчанию.
    const [learningMethod, setLearningMethod] = useState(null);
    // Порог и диапазон принадлежат освоению по критерию. У методик расписания таких
    // настроек нет вовсе, и показывать их пустыми — хуже, чем не показывать.
    const isMasteryMethod = learningMethod === QUIZ_LEARNING_METHOD;

    // Состояния для режимов настройки
    const [isRealSettingsActive, setIsRealSettingsActive] = useState(false);
    // Что было до входа в настройки экзамена: «отменить» обязано вернуть именно это.
    // Раньше обе кнопки просто закрывали режим, то есть «отменить» ничего не отменяло.
    const [initialRealDTO, setInitialRealDTO] = useState(null);
    const [isPracticeSettingsActive, setIsPracticeSettingsActive] = useState(false);

    // Состояние для хранения первоначальных настроек (для "Cancel")
    const [initialPoolDTO, setInitialPoolDTO] = useState(null);
    // Номер запроса на открытие практики: методику можно переключить дважды подряд, и
    // ответ на первый запрос не должен перезаписать результат второго.
    const initRequest = useRef(0);


    const handleRangeSliderChange = (value) => {
        const [newStart, newFinish] = value;
        const newQuizRangeSize = newFinish - newStart + 1;
        const updatedPoolSize = Math.min(poolDTO.poolSize, newQuizRangeSize);
        setPoolDTO((prevState) => ({
            ...prevState,
            startQuizNumber: newStart,
            finishQuizNumber: newFinish,
            poolSize: updatedPoolSize,
        }));
    };

    const handleSliderChange = (value, sliderName) => {
        setPoolDTO((prevState) => ({
            ...prevState,
            [sliderName]: value,
        }));
    };

    const handleRealInputChange = (value, field) => {
        setRealDTO((prevState) => ({
            ...prevState,
            [field]: value,
        }));
    };

    const handleRealRangeChange = (value) => {
        setRealDTO((prevState) => ({
            ...prevState,
            questionRange: value,
            // Сузили диапазон — число вопросов не может остаться больше него.
            numberOfQuestions: Math.min(prevState.numberOfQuestions, value[1] - value[0] + 1),
        }));
    };

    /**
     * Открыть практику и уйти на прохождение. Именно по кнопке: страница выбора ничего
     * не создаёт, пока её только смотрят, — раньше init висел на монтировании и на
     * каждом переключении методики, то есть документ практики заводился и сохранялся
     * при каждом показе страницы.
     *
     * @param sessionMode чем набирать пул; пусто — как обычно
     */
    /**
     * Занятие по плану к дате. Свой признак загрузки: кнопка плана живёт в панели, и
     * крутиться должна она, а не «начать» в карточке практики.
     */
    const startPlanned = async () => {
        setIsStartingPlanned(true);
        try {
            await startPractice('DEADLINE_REVIEW');
        } finally {
            setIsStartingPlanned(false);
        }
    };

    const startPractice = async (sessionMode) => {
        if (!selectedDeck?.deckId) {
            message.error(t('deck.noneSelected'));
            return;
        }
        if (!learningMethod) {
            return;
        }
        const request = ++initRequest.current;
        setIsLoading(true);
        try {
            const response = await practiceClient.initializePractice({
                deckId: selectedDeck.deckId,
                practiceType: QUIZ_PRACTICE_TYPE,
                practiceFormat: QUIZ_PRACTICE_FORMAT,
                learningMethod,
                ...(sessionMode ? { sessionMode } : {}),
            });
            if (request !== initRequest.current) {
                // Пока запрос шёл, методику переключили: этот ответ описывает практику,
                // которой на экране уже нет.
                return;
            }
            setPracticeId(response.practiceId);
            dispatch(setPractice(
                response.practiceId, QUIZ_PRACTICE_TYPE, QUIZ_PRACTICE_FORMAT, learningMethod));
            navigate('/quiz-run');
        } catch (error) {
            console.error('Error initializing practice:', error);
            message.error(t('practice.initQuizFailed'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleApplyPracticeSettings = async () => {
        if (!selectedDeck?.deckId) {
            message.error(t('deck.noneSelected'));
            return;
        }

        setIsLoading(true);
        // Тот же номер запроса, что и у открытия: применение настроек создаёт практику
        // так же, и его ответ так же может опоздать за переключением методики.
        const request = ++initRequest.current;
        const isCurrent = () => request === initRequest.current;

        const practiceRequest = {
            deckId: selectedDeck.deckId,
            practiceType: QUIZ_PRACTICE_TYPE,
            practiceFormat: QUIZ_PRACTICE_FORMAT,
            learningMethod,
            // Порог и диапазон принадлежат критерию освоения: у методик расписания
            // таких полей в запросе нет, и слать их значило бы описывать практику
            // настройками, которых у неё не будет.
            ...(learningMethod === QUIZ_LEARNING_METHOD ? {
                poolSize: poolDTO.poolSize,
                requiredCorrectAnswers: poolDTO.correctAnswerLevel,
                rangeStart: poolDTO.startQuizNumber,
                rangeEnd: poolDTO.finishQuizNumber,
            } : {}),
        };

        try {
            const response = await practiceClient.initializePractice(practiceRequest);
            if (!isCurrent()) {
                // Пока запрос шёл, методику переключили ещё раз: этот ответ описывает
                // практику, которой на экране уже нет.
                return;
            }

            setPracticeId(response.practiceId);
            dispatch(setPractice(
                response.practiceId, QUIZ_PRACTICE_TYPE, QUIZ_PRACTICE_FORMAT, learningMethod));
            // Что получилось, скажет сводка: сервер мог обрезать диапазон по размеру
            // колоды, и подставлять сюда присланное значило бы показать не то, что
            // записано.
            reload();
            message.success(t('practice.settingsApplied'));
        } catch (error) {
            console.error('Error applying practice settings:', error);
            message.error(t('practice.settingsApplyFailed'));
        } finally {
            setIsLoading(false);
            setIsPracticeSettingsActive(false);
        }
    };

    // Диапазон экзамена по умолчанию — вся колода: до сводки её размер неизвестен.
    const deckSize = summary?.itemCount ?? 0;
    useEffect(() => {
        if (deckSize <= 0) {
            return;
        }
        setRealDTO((previous) => (previous.questionRange[1] > 1 ? previous : {
            ...previous,
            questionRange: [1, deckSize],
            numberOfQuestions: Math.min(previous.numberOfQuestions, deckSize),
        }));
    }, [deckSize]);

    // Размер колоды — из сводки, а не из ответа на открытие практики: он не зависит от
    // методики, а в ответе есть только у критерия освоения.
    const totalQuizzes = summary?.itemCount ?? poolDTO.totalQuizzes;

    /**
     * Сколько вопросов может быть в пробном экзамене.
     * <p>
     * Столько, сколько их в выбранном диапазоне, но не больше трёхсот — потолок сервера:
     * экзамен на тысячу вопросов это не экзамен, а опечатка. Раньше здесь стоял размер
     * блока практики, то есть по умолчанию пять: настройка экзамена ограничивалась
     * числом, которое к экзамену отношения не имеет, а значение по умолчанию —
     * шестьдесят пять — оказывалось за пределами собственного ползунка.
     */
    const examQuestionsMax = Math.min(
        300,
        Math.max(1, realDTO.questionRange[1] - realDTO.questionRange[0] + 1));

    // Параметры отбора приходят из сводки — она их знает и ничего не создаёт. Раньше
    // страница узнавала их единственным способом: открывала практику при каждом показе
    // и при каждом переключении методики. Ровно от этого сводку и завели.
    useEffect(() => {
        // Строго своя практика: `practiceFor` при общем состоянии подставляет соседнюю
        // методику, чтобы не обнулять счётчики формата, — но параметры отбора и ссылка
        // на статистику принадлежат конкретному документу, и чужой здесь означал бы
        // чужие ползунки и ссылку не туда.
        const practice = (summary?.practices ?? []).find(
            (row) => row.practiceFormat === QUIZ_PRACTICE_FORMAT
                && row.learningMethod === learningMethod) ?? null;
        setPracticeId(practice?.practiceId ?? null);
        if (practice?.poolSize == null) {
            return;
        }
        setPoolDTO((previous) => ({
            ...previous,
            startQuizNumber: practice.rangeStart ?? previous.startQuizNumber,
            finishQuizNumber: practice.rangeEnd ?? previous.finishQuizNumber,
            poolSize: practice.poolSize ?? previous.poolSize,
            correctAnswerLevel: practice.requiredCorrectAnswers ?? previous.correctAnswerLevel,
        }));
    }, [summary, learningMethod]);

    /**
     * Пробный экзамен. Не практика: фиксированный набор, время, без подсказок и без
     * влияния на расписание. Раньше эта кнопка показывала сообщение и ничего не делала.
     */
    const handleStartRealExam = async () => {
        if (!selectedDeck?.deckId) {
            message.error(t('deck.noneSelected'));
            return;
        }
        setIsLoading(true);
        try {
            const exam = await practiceClient.startExam({
                deckId: selectedDeck.deckId,
                practiceType: QUIZ_PRACTICE_TYPE,
                practiceFormat: QUIZ_PRACTICE_FORMAT,
                questionCount: realDTO.numberOfQuestions,
                durationMinutes: realDTO.examDuration,
                rangeStart: realDTO.questionRange[0],
                rangeEnd: realDTO.questionRange[1],
            });
            dispatch(setExam(exam));
            navigate('/exam-run');
        } catch (error) {
            console.error('Error starting mock exam:', error);
            message.error(t('exam.startFailed'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggleRealSettings = () => {
        if (!isRealSettingsActive) {
            setInitialRealDTO(realDTO);
        }
        setIsRealSettingsActive(!isRealSettingsActive);
    };

    const handleCancelRealSettings = () => {
        if (initialRealDTO) {
            setRealDTO(initialRealDTO);
        }
        setIsRealSettingsActive(false);
    };

    const handleTogglePracticeSettings = () => {
        if (!isPracticeSettingsActive) {
            // Сохраняем текущие настройки перед входом в режим настройки
            setInitialPoolDTO(poolDTO);
            // Режим настройки выключили, не применив изменения: возвращаем сохранённое
        } else if (initialPoolDTO) {
            setPoolDTO(initialPoolDTO);
            setRealDTO((prevState) => ({
                ...prevState,
                questionRange: [initialPoolDTO.startQuizNumber, initialPoolDTO.finishQuizNumber],
                numberOfQuestions: initialPoolDTO.poolSize,
            }));
            message.info(t('practice.settingsChangesCancelled'));
        }
        setIsPracticeSettingsActive(!isPracticeSettingsActive);
    };

    const handleApplySettings = () => {
        handleApplyPracticeSettings();
    };

    const handleCancelPracticeSettings = () => {
        if (initialPoolDTO) {
            setPoolDTO(initialPoolDTO);
            setRealDTO((prevState) => ({
                ...prevState,
                questionRange: [initialPoolDTO.startQuizNumber, initialPoolDTO.finishQuizNumber],
                numberOfQuestions: initialPoolDTO.poolSize,
            }));
            setIsPracticeSettingsActive(false);
            message.info(t('practice.settingsCancelled'));
        }
    };

    // Обработчик для перехода на страницу статистики с передачей параметров
    const handleRefreshPracticeStatistic = () => {
        if (selectedDeck?.deckId && practiceId) {
            navigate("/stat", { state: { deckId: selectedDeck.deckId, practiceId: practiceId } });
        } else {
            message.info(t('practice.noStatistics'));
        }
    };

    return (
        <div style={{ padding: '10px', display: 'flex', justifyContent: 'center' }}>
            <Row
                gutter={[30, 30]}
                justify="center"
                align="top"
                style={{ width: '100%', display: 'flex', alignItems: 'stretch'}}
            >
                {/* Practice Card */}
                <Col xs={24} sm={12} md={8}>
                    <Card
                        bordered
                        style={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            position: 'relative'
                        }}
                    >
                        <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', alignItems: 'center' }}>
                            <Tooltip title={t('practice.showStatistics')} placement="topRight">
                                <BarChartOutlined
                                    style={{ marginRight: '8px', fontSize: '20px', cursor: 'pointer', color: '#808080' }}
                                    onClick={handleRefreshPracticeStatistic}
                                />
                            </Tooltip>
                            <Tooltip title={t('practice.setupPractice')} placement="topRight">
                                <SettingOutlined
                                    style={{
                                        fontSize: '20px',
                                        cursor: isMasteryMethod ? 'pointer' : 'not-allowed',
                                        color: isMasteryMethod ? '#808080' : '#d0d4d6',
                                    }}
                                    onClick={isMasteryMethod ? handleTogglePracticeSettings : undefined}
                                />
                            </Tooltip>
                        </div>

                        <div style={{ flex: '1 0 auto', paddingTop: '0px' }}>
                            <Title level={3}>{t('practice.title')}</Title>
                            <Paragraph>
                                {t('practice.quizDeckSummary', { count: totalQuizzes })}
                            </Paragraph>
                            {isMasteryMethod && (
                            <Paragraph>
                                {t('practice.quizPracticeSummary', {
                                    poolSize: poolDTO.poolSize,
                                    correctAnswers: poolDTO.correctAnswerLevel,
                                    start: poolDTO.startQuizNumber,
                                    finish: poolDTO.finishQuizNumber,
                                })}
                            </Paragraph>
                            )}

                            {isMasteryMethod && (
                            <div style={{ marginTop: '20px', textAlign: 'left' }}>
                                <Text>{t('practice.poolSize', { count: poolDTO.poolSize })}</Text>
                                <Slider
                                    min={1}
                                    max={poolDTO.finishQuizNumber - poolDTO.startQuizNumber + 1}
                                    step={1}
                                    value={poolDTO.poolSize}
                                    onChange={(value) => handleSliderChange(value, 'poolSize')}
                                    tooltip={{ formatter: (value) => `${value}` }}
                                    disabled={!isPracticeSettingsActive}
                                />
                            </div>
                            )}

                            {isMasteryMethod && (
                            <div style={{ marginTop: '20px', textAlign: 'left' }}>
                                <Text>{t('practice.correctAnswerLevel', { count: poolDTO.correctAnswerLevel })}</Text>
                                <Slider
                                    min={1}
                                    max={15}
                                    step={1}
                                    value={poolDTO.correctAnswerLevel}
                                    onChange={(value) => handleSliderChange(value, 'correctAnswerLevel')}
                                    tooltip={{ formatter: (value) => `${value}` }}
                                    disabled={!isPracticeSettingsActive}
                                />
                            </div>
                            )}

                            {isMasteryMethod && (
                            <div style={{ marginTop: '20px', textAlign: 'left' }}>
                                <Text>{t('practice.quizRange', { start: poolDTO.startQuizNumber, finish: poolDTO.finishQuizNumber })}</Text>
                                <Slider
                                    range
                                    min={1}
                                    max={totalQuizzes || 14}
                                    value={[poolDTO.startQuizNumber, poolDTO.finishQuizNumber]}
                                    onChange={handleRangeSliderChange}
                                    tooltip={{ formatter: (value) => `${value}` }}
                                    disabled={!isPracticeSettingsActive}
                                />
                            </div>
                            )}
                        </div>
                        <div style={{ marginTop: '20px' }}>
                            {isPracticeSettingsActive ? (
                                <Space style={{ width: '100%', justifyContent: 'center' }}>
                                    <Button type="primary" onClick={handleApplySettings}>
                                        {t('common.apply')}
                                    </Button>
                                    <Button onClick={handleCancelPracticeSettings}>
                                        {t('common.cancel')}
                                    </Button>
                                </Space>
                            ) : (
                                <Space.Compact block>
                                    <Button
                                        type="primary"
                                        block
                                        onClick={() => startPractice()}
                                        loading={isLoading}
                                        disabled={isLoading || !learningMethod}
                                    >
                                        {t('practice.start')}
                                    </Button>
                                    {/* Только у методик со сроком: у освоения по критерию
                                        извлекаемости нет, и набирать пул по риску не из чего. */}
                                    {learningMethod && learningMethod !== QUIZ_LEARNING_METHOD && (
                                        <Tooltip title={t('practice.riskReview')}>
                                            <Button
                                                type="primary"
                                                aria-label={t('practice.riskReview')}
                                                icon={<ThunderboltOutlined />}
                                                loading={isLoading}
                                                disabled={isLoading || !learningMethod}
                                                onClick={() => startPractice('RISK_REVIEW')}
                                            />
                                        </Tooltip>
                                    )}
                                </Space.Compact>
                            )}
                        </div>
                    </Card>
                </Col>

                {/* Real Card */}
                <Col xs={24} sm={12} md={8}>
                    {/* Область с именем: карточку экзамена можно найти по роли, а не по
                        обходу DOM, — и читалкой, и тестом. */}
                    <Card
                        bordered
                        role="region"
                        aria-label={t('practice.realTitle')}
                        style={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            position: 'relative'
                        }}
                    >
                        <Tooltip title={t('practice.setupRealExam')} placement="topRight">
                            <SettingOutlined
                                style={{ position: 'absolute', top: 10, right: 10, fontSize: '20px', cursor: 'pointer', color: '#808080' }}
                                onClick={handleToggleRealSettings}
                            />
                        </Tooltip>

                        <div style={{ flex: '1 0 auto' }}>
                            <Title level={3}>{t('practice.realTitle')}</Title>
                            <Paragraph>
                                {t('practice.quizDeckSummary', { count: totalQuizzes })}
                            </Paragraph>
                            <Paragraph>
                                {t('practice.realSummary', {
                                    questions: realDTO.numberOfQuestions,
                                    minutes: realDTO.examDuration,
                                })}
                            </Paragraph>

                            <Form layout="vertical" style={{ marginTop: '20px' }}>
                                <Form.Item label={t('practice.examDuration')} required>
                                    <Slider
                                        min={10}
                                        max={300}
                                        step={5}
                                        value={realDTO.examDuration}
                                        onChange={(value) => handleRealInputChange(value, 'examDuration')}
                                        tooltip={{ formatter: (value) => t('practice.minutesShort', { count: value }) }}
                                        disabled={!isRealSettingsActive}
                                    />
                                </Form.Item>

                                <Form.Item label={t('practice.numberOfQuestions')} required>
                                    <Slider
                                        min={1}
                                        max={examQuestionsMax}
                                        step={1}
                                        value={realDTO.numberOfQuestions}
                                        onChange={(value) => handleRealInputChange(value, 'numberOfQuestions')}
                                        tooltip={{ formatter: (value) => t('practice.questions', { count: value }) }}
                                        disabled={!isRealSettingsActive}
                                    />
                                </Form.Item>

                                <Form.Item label={t('practice.questionRange')}>
                                    <Slider
                                        range
                                        min={1}
                                        max={totalQuizzes || 290}
                                        value={realDTO.questionRange}
                                        onChange={handleRealRangeChange}
                                        tooltip={{ formatter: (value) => `${value}` }}
                                        disabled={!isRealSettingsActive}
                                    />
                                </Form.Item>
                            </Form>
                        </div>
                        <div style={{ marginTop: '20px' }}>
                            {isRealSettingsActive ? (
                                <Space style={{ width: '100%', justifyContent: 'center' }}>
                                    <Button type="primary" onClick={() => setIsRealSettingsActive(false)}>
                                        {t('common.apply')}
                                    </Button>
                                    <Button onClick={handleCancelRealSettings}>
                                        {t('common.cancel')}
                                    </Button>
                                </Space>
                            ) : (
                                <Button
                                    block
                                    onClick={handleStartRealExam}
                                    loading={isLoading}
                                >
                                    {t('practice.startRealExam')}
                                </Button>
                            )}
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={24} md={8}>
                    {/* Настройки обучения общие для всех колод: удержка действует всюду,
                        где выбраны адаптивные интервалы, а до этого задать её можно было
                        только со страницы словаря. */}
                    {/* План к дате: у методик со сроком он считается, у критерия освоения
                        панель честно говорит, что считать нечего — срока он не назначает. */}
                    <DeadlinePanel
                        deckId={selectedDeck?.deckId}
                        practiceType={QUIZ_PRACTICE_TYPE}
                        isStarting={isStartingPlanned}
                        onStartPlanned={startPlanned}
                    />
                    <LearningSettingsPanel />
                    <LearningMethodPanel
                        deckId={selectedDeck?.deckId}
                        practiceType={QUIZ_PRACTICE_TYPE}
                        onMethodChange={setLearningMethod}
                    />
                </Col>
            </Row>
        </div>
    );
};

export default QuizLearnPage;
